import type { CityConfig } from "../city-template/types";

export const wheelingConfig: CityConfig = {
  city: "Wheeling",
  state: "West Virginia",
  stateAbbr: "WV",
  slug: "wheeling",
  route: "/wheeling",
  title: "Emergency Passport Wheeling, WV — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Wheeling, WV — as fast as 24 hours. A+ BBB rated, registered State Dept. courier serving Ohio County, Northern Panhandle, and Ohio Valley travelers via Pittsburgh International.",
  h1: "Emergency & Expedited Passport Services in Wheeling, WV",
  heroSubtitle: "International business trip from Pittsburgh to London in 10 days? WesBanco corporate meeting in Dublin? Wheeling Jesuit University study abroad in Rome? We help Wheeling residents — from Victorian Old Town professionals to Bethlehem families to Ohio Valley business travelers — get their passports fast. As a registered U.S. Department of State courier, we offer a best price guarantee and rates 30–100% lower than FedEx, Staples, and other third-party resellers. As fast as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Beautiful%20panoramic%20view%20of%20Wheeling%20West%20Virginia%20along%20the%20Ohio%20River%20with%20the%20historic%20Wheeling%20Suspension%20Bridge%20and%20Victorian%20architecture%20in%20the%20foreground%2C%20rolling%20Appalachian%20hills%20in%20the%20background%20under%20warm%20golden%20hour%20sunlight%2C%20riverfront%20downtown%20district%20with%20brick%20buildings%20and%20church%20steeples%2C%20serene%20Ohio%20Valley%20landscape%20with%20autumn%20colors&width=1800&height=700&seq=wheeling-hero-2026&orientation=landscape",
  heroImageAlt: "Wheeling West Virginia with Suspension Bridge and Ohio River at golden hour",
  geoPosition: "40.0640;-80.7209",
  geoPlacename: "Wheeling, West Virginia",
  lastModified: "2026-06-07",
  regionalAgency: {
    name: "Washington Passport Agency",
    address: "600 19th St NW, Washington, DC 20006",
    distance: "Approximately 280 miles from Wheeling — appointments required, typically booked 2+ weeks out",
  },
  nearbyCitiesZips: [
    { name: "Wheeling", zips: "26003" },
    { name: "Bethlehem", zips: "26003" },
    { name: "Triadelphia", zips: "26059" },
    { name: "Valley Grove", zips: "26060" },
    { name: "Elm Grove", zips: "26003" },
    { name: "Moundsville", zips: "26041" },
    { name: "Weirton", zips: "26062" },
    { name: "St. Clairsville, OH", zips: "43950" },
    { name: "Steubenville, OH", zips: "43952" },
    { name: "Washington, PA", zips: "15301" },
  ],
  benefits: [
    { icon: "ri-flight-takeoff-line", title: "Pittsburgh Airport Gateway — 60 Miles", desc: "Wheeling is just 60 miles from Pittsburgh International Airport (PIT), the primary international departure point. We coordinate your passport timeline with the PIT drive and airline check-in requirements." },
    { icon: "ri-building-2-line", title: "Corporate & Financial Travel", desc: "Wheeling is home to WesBanco, Orrick, Williams Lea, and Ohio Valley corporate offices. When international business calls on short notice, we ensure passports are ready." },
    { icon: "ri-landscape-line", title: "Historic City, Modern Travel Needs", desc: "Wheeling's Victorian architecture and heritage tourism draw visitors — but the same residents also travel internationally. We bring modern expedited passport service to this historic Ohio Valley city." },
    { icon: "ri-government-line", title: "Northern Panhandle Hub", desc: "As the largest city in West Virginia's Northern Panhandle, Wheeling serves as the regional center for Ohio, Marshall, and Brooke counties — we serve the entire tri-state area." },
  ],
  testimonials: [
    {
      name: "Catherine D.",
      location: "Wheeling Island",
      rating: 5,
      text: "I had a business trip to Dublin with just 11 days notice. The WesBanco travel desk booked my flight but didn't check my passport — it was expired. Fast Passport Center got it renewed in 5 days. Saved my quarterly review meeting.",
      service: "Emergency Passport Renewal",
    },
    {
      name: "The Morrison Family",
      location: "Elm Grove",
      rating: 5,
      text: "Family trip to Italy — three of us needed new passports. The checklist was clear, the post office visit was smooth, and all three passports arrived within 10 days. Incredible service for a family of five.",
      service: "Family Passport — New & Renewals",
    },
    {
      name: "Dr. James P.",
      location: "Woodsdale, Wheeling",
      rating: 5,
      text: "Medical conference in Vienna with 8 days notice. They handled everything remotely and my passport arrived in 4 business days. I presented my research on schedule.",
      service: "Emergency Passport Renewal",
    },
    {
      name: "Linda S.",
      location: "Bethlehem",
      rating: 5,
      text: "Retirement trip to Scotland — my first passport renewal in 20 years. I was nervous about the process but they made it simple. Passport arrived in 7 days with regular updates throughout.",
      service: "Passport Renewal",
    },
  ],
  facilities: [
    {
      id: "wheeling-1",
      name: "Wheeling Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2501 Chapline St",
      city: "Wheeling",
      state: "WV",
      zip: "26003",
      phone: "(304) 233-3604",
      hours: "Mon–Fri 9:00 AM – 4:00 PM (appointment required)",
      distance: "Downtown Wheeling — Chapline Street",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location",
      notes: "Primary acceptance facility for Wheeling and Ohio County. Passport photos available on-site. Central location in downtown Wheeling near the historic district.",
      icon: "ri-mail-line",
    },
    {
      id: "wheeling-2",
      name: "Ohio County Circuit Clerk",
      type: "County Clerk Passport Acceptance",
      address: "1500 Chapline St, Room 115",
      city: "Wheeling",
      state: "WV",
      zip: "26003",
      phone: "(304) 234-3720",
      hours: "Mon–Fri 8:30 AM – 4:30 PM",
      distance: "Downtown Wheeling — City-County Building",
      website: "https://www.ohiocountywv.gov/circuit-clerk",
      websiteLabel: "Ohio County Clerk",
      notes: "County clerk passport acceptance counter in the City-County Building. Often less crowded than the post office. No photo services — obtain passport photos before visiting.",
      icon: "ri-government-line",
    },
    {
      id: "wheeling-3",
      name: "St. Clairsville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "141 E Main St",
      city: "St. Clairsville",
      state: "OH",
      zip: "43950",
      phone: "(740) 695-0580",
      hours: "Mon–Fri 9:00 AM – 4:00 PM",
      distance: "St. Clairsville, OH — 8 miles west",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location",
      notes: "Alternative facility for eastern Ohio County residents and those near the Ohio border. Serves Belmont County. Photo services available.",
      icon: "ri-mail-line",
    },
    {
      id: "wheeling-4",
      name: "Moundsville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "600 7th St",
      city: "Moundsville",
      state: "WV",
      zip: "26041",
      phone: "(304) 845-0420",
      hours: "Mon–Fri 9:00 AM – 3:00 PM",
      distance: "Moundsville — 11 miles south, Marshall County",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location",
      notes: "Serving Marshall County and southern Ohio County. Limited afternoon hours — call ahead to confirm availability.",
      icon: "ri-mail-line",
    },
    {
      id: "wheeling-5",
      name: "Weirton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "100 Saint Thomas Dr",
      city: "Weirton",
      state: "WV",
      zip: "26062",
      phone: "(304) 748-8011",
      hours: "Mon–Fri 9:00 AM – 4:00 PM (appointment required)",
      distance: "Weirton — 25 miles north, Hancock County",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location",
      notes: "Serving Hancock County and northern panhandle. Alternative to Wheeling post office for northern Ohio County and Brooke County residents.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    { q: "How fast can I get a passport in Wheeling?", a: "We offer Wheeling-area clients expedited processing as fast as 24 hours. Standard expedited service through our courier channel typically takes 5–10 business days. There is no passport agency in Wheeling — the nearest is Washington, DC, 280 miles away. Our courier service eliminates the need for a DC trip entirely." },
    { q: "Do I need to visit an office in Wheeling?", a: "No. Our service is remote — you complete forms online, visit a local acceptance facility (Wheeling Main Post Office or Ohio County Clerk at the City-County Building), then ship documents overnight. We handle the State Department submission and return your passport to your Wheeling address." },
    { q: "Which airport should Wheeling travelers use?", a: "Pittsburgh International Airport (PIT) is the primary international gateway — just 60 miles east via I-70. PIT offers direct flights to London, Paris, Frankfurt, Cancun, and 50+ international destinations. The drive takes about 60–75 minutes. Some travelers also use Columbus (CMH, ~130 miles) or Cleveland (CLE, ~140 miles)." },
    { q: "What is the best price for expedited passport service in Wheeling?", a: "Government fees are $130 + $60 expedite fee. Our courier fee covers document review, overnight shipping, and State Department submission. Total cost is typically 30–100% lower than FedEx, Staples, and other third-party resellers. Best price guarantee included." },
    { q: "Where can I get a passport photo in Wheeling?", a: "CVS Pharmacy on National Road, Walgreens on Warwood Avenue, The UPS Store on Chapline Street, and Walmart on Mount de Chantal Road all offer passport photos. The Wheeling Main Post Office also provides on-site photo services." },
    { q: "What documents do Wheeling first-time applicants need?", a: "Certified birth certificate, government-issued photo ID (WV driver's license or state ID), completed DS-11 form, and passport photo. Many Wheeling-area residents were born in Ohio hospitals — if so, you will need Ohio vital records, not West Virginia. We provide a personalized checklist." },
    { q: "Can I use the Ohio County Clerk for my passport acceptance?", a: "Yes — the Ohio County Circuit Clerk at the City-County Building (1500 Chapline St, Room 115) accepts passport applications during regular business hours. It is often less crowded than the post office and is conveniently located downtown." },
    { q: "What about international travel from Wheeling for business?", a: "Wheeling's corporate sector — WesBanco, Orrick, Williams Lea, and Ohio Valley businesses — regularly sends employees to the UK, Ireland, Germany, and Canada. Short-notice travel (1–2 weeks) is common. We specialize in predictable, fast processing for business travelers." },
    { q: "Does Wheeling Jesuit University offer passport help?", a: "Wheeling Jesuit University does not have a dedicated passport office, but the Wheeling Main Post Office and Ohio County Clerk are both within a short drive of campus. We provide student-specific guidance for study abroad, mission trips, and international service programs." },
    { q: "How close is Wheeling to the Pittsburgh Passport Agency?", a: "There is no passport agency in Pittsburgh — the nearest is Washington, DC, 280 miles away. Pittsburgh International Airport (PIT) is 60 miles from Wheeling, but it does not have a passport agency. Our courier service submits directly to the DC Passport Agency, so you never need to make that 280-mile drive." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — National Road", address: "2101 National Rd, Wheeling, WV 26003", hours: "Daily 8 AM – 9 PM" },
    { name: "Walgreens — Warwood Ave", address: "2201 Warwood Ave, Wheeling, WV 26003", hours: "Daily 8 AM – 10 PM" },
    { name: "The UPS Store — Chapline St", address: "1101 Main St, Wheeling, WV 26003", hours: "Mon–Fri 8:30 AM – 6 PM, Sat 9 AM – 1 PM" },
    { name: "Walmart — Mount de Chantal Rd", address: "770 Horizon Dr, Wheeling, WV 26003", hours: "Daily 6 AM – 11 PM" },
    { name: "Wheeling Main Post Office", address: "2501 Chapline St, Wheeling, WV 26003", hours: "Mon–Fri 9 AM – 4 PM" },
  ],
  dmvLocations: [
    {
      name: "WV DMV — Wheeling Regional Office",
      address: "49 23rd St, Wheeling, WV 26003",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Driver licenses, REAL ID, state ID cards. Appointment recommended — walk-ins accepted.",
      featured: true,
    },
  ],
  dmvWebsite: "https://transportation.wv.gov/dmv/",
  dmvLabel: "West Virginia DMV — Official Website",
  airports: [
    {
      code: "PIT",
      name: "Pittsburgh International Airport",
      distance: "About 60 miles east of Wheeling",
      address: "1000 Airport Blvd, Pittsburgh, PA 15231",
      note: "Primary international gateway for Wheeling and the Northern Panhandle. Direct flights to London, Paris, Frankfurt, Cancun, and 50+ destinations. About 60–75 minutes via I-70 East.",
      flightUrl: "https://flypittsburgh.com/",
      flightLabel: "PIT Airport Website",
      featured: true,
    },
    {
      code: "HLG",
      name: "Wheeling Ohio County Airport",
      distance: "About 10 miles northeast of Wheeling",
      address: "100 Aviation Way, Wheeling, WV 26003",
      note: "General aviation airport — no commercial passenger service. Used by private and corporate aircraft only. Not suitable for commercial international departures.",
      flightUrl: "https://www.ohiocountywv.gov/airport",
      flightLabel: "HLG Airport Website",
      featured: false,
    },
    {
      code: "CMH",
      name: "John Glenn Columbus International Airport",
      distance: "About 130 miles west of Wheeling",
      address: "4600 International Gateway, Columbus, OH 43219",
      note: "Alternative international gateway. Direct flights to Cancun, Toronto, and seasonal European destinations. About 2 hours via I-70 West.",
      flightUrl: "https://flycolumbus.com/",
      flightLabel: "CMH Airport Website",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      icon: "ri-user-smile-line",
      label: "Birth Certificate — WV DHHR",
      desc: "Request certified birth certificates from the West Virginia DHHR Vital Registration Office. Online, mail, or in-person options available.",
      url: "https://dhhr.wv.gov/bph/hsc/vital/Pages/default.aspx",
    },
    {
      icon: "ri-hearts-line",
      label: "Marriage Certificate — Ohio County",
      desc: "Request marriage license records from the Ohio County Clerk. Certified copies for name changes and passport applications.",
      url: "https://www.ohiocountywv.gov/county-clerk",
    },
    {
      icon: "ri-file-copy-line",
      label: "Divorce Decree — Ohio County",
      desc: "Obtain certified divorce records through the Ohio County Circuit Clerk. Required for name change passport corrections.",
      url: "https://www.ohiocountywv.gov/circuit-clerk",
    },
    {
      icon: "ri-file-list-2-line",
      label: "Death Certificate — WV DHHR",
      desc: "Certified death certificates through WV DHHR Vital Registration for estate and legal purposes.",
      url: "https://dhhr.wv.gov/bph/hsc/vital/Pages/default.aspx",
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Pittsburgh flight to London in two days? Corporate board meeting in Dublin this week? Our emergency courier service submits directly via reserved agency slots — as fast as 24 hours.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already hold a U.S. passport? We help Wheeling residents renew fast — no agency visit required for most renewals. Perfect for Ohio Valley business travelers and Northern Panhandle families.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport — First Time",
      description: "First-time applicants require an in-person acceptance clerk visit. We guide Wheeling residents through every step with clear instructions for the nearest acceptance location.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements require additional forms. We guide you through DS-64 and everything needed to avoid costly mistakes — so your business trip or family vacation stays on track.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors require parental participation and an in-person acceptance step. We provide clear checklists so your family vacation abroad stays on schedule.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or legal name change — we assist with corrected passports so travel documents match reservations and IDs.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport",
      description: "Need a second passport for frequent international travel? Ideal for corporate executives and consultants traveling while a primary passport is in visa processing.",
      bullets: ["Frequent traveler solutions", "Backup travel document", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
    {
      icon: "ri-file-damage-line",
      title: "Damaged Passport",
      description: "Replacing a significantly damaged passport is faster than you think. We guide Wheeling-area travelers through the process.",
      bullets: ["Damaged passport replacement", "Document review included", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=damaged-passport",
    },
    {
      icon: "ri-id-card-line",
      title: "Passport Card",
      description: "Wallet-size travel document for land and sea crossings to Canada, Mexico, the Caribbean, and Bermuda — expedited with the same courier service as book passports.",
      bullets: ["Wallet-size convenience", "Land and sea travel", "Same expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-card",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Wheeling WV", description: "Urgent passport processing for Wheeling West Virginia travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Wheeling WV", description: "Fast passport renewal for Wheeling West Virginia residents." },
    { name: "First-Time Passport Wheeling WV", description: "New passport application services for Wheeling West Virginia residents." },
    { name: "Child Passport Wheeling WV", description: "Minor passport services for Wheeling West Virginia families." },
    { name: "Name Change Passport Wheeling WV", description: "Passport name correction after marriage, divorce, or legal name changes." },
    { name: "Second Passport Wheeling WV", description: "Second passport services for Wheeling corporate and international travelers." },
  ],
  mistakes: [
    {
      icon: "ri-error-warning-line",
      title: "Underestimating PIT Drive + Airport Time",
      description: "Wheeling travelers often forget that reaching PIT means 60 miles of driving (~75 minutes) plus 3 hours for international check-in. A passport arriving the day before departure leaves almost no safety margin if anything goes wrong with weather, traffic, or airport lines.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Using Ohio Birth Certificates for WV Residents",
      description: "Many Wheeling-area residents were born in Ohio hospitals (across the river in Bridgeport, St. Clairsville, or Steubenville). When ordering birth certificates for passport applications, you must request from Ohio vital records — not West Virginia. We flag this during document review.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Photo Standards Violations",
      description: "Photos from Wheeling-area pharmacies frequently fail State Department requirements — background not pure white, shadows, wrong size. We review your photo before submission to prevent rejection.",
    },
    {
      icon: "ri-error-warning-line",
      title: "DS-82 When DS-11 Is Required",
      description: "Wheeling applicants often try the simpler renewal form when their passport was issued at age 15 or younger, or expired more than 5 years ago. A rejected form costs 1–2 weeks. Our review catches this before submission.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Cross-State Document Confusion",
      description: "Wheeling sits on the WV-OH border, and many residents work, shop, and access services in both states. This creates confusion about which state's vital records and DMV to use. We provide clear, personalized guidance based on your specific situation.",
    },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order — just a few minutes from home or office." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist tailored to your travel situation and departure airport." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using overnight shipping. Most Wheeling clients use the UPS Store on Chapline Street or ship from home." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency channel — no need to drive 280 miles to Washington, DC." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport arrives at your Wheeling address within your selected timeframe — no DC drive, no appointment wait." },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned", "Daily status updates"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "In-person clerk appointment required", "No document review", "No dedicated agent", "Updates only by mail", "Can't upgrade speed after submission"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — nearest agency is Washington DC (280 miles)", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — nearest is Washington DC, 280 miles away. Booked 2+ weeks out", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Required — drive 280 miles to Washington DC", fpc: "Most Wheeling travelers ship documents overnight" },
    { feature: "Processing Speed", agency: "Life-or-death emergencies only", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission" },
    { feature: "Local Guidance", agency: "Generic — no PIT airport, corporate, or cross-state document expertise", fpc: "Full Wheeling/PIT/Ohio Valley knowledge" },
    { feature: "Cost vs Resellers", agency: "Government fees only — you handle all logistics", fpc: "Best price guarantee — 30-100% less than FedEx, Staples & resellers" },
  ],
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Local Area", href: "#service-area" },
    { label: "Travel", href: "#travel-timing" },
    { label: "Destinations", href: "#destinations" },
    { label: "Reviews", href: "#testimonials" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Compare", href: "#comparison" },
    { label: "Mistakes", href: "#mistakes" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Fast passport services in Charleston, WV", route: "/charleston" },
    { name: "Expedited passport in Morgantown, WV", route: "/morgantown" },
    { name: "Quick passport services in Parkersburg, WV", route: "/parkersburg" },
    { name: "Urgent passport services in Weirton, WV", route: "/weirton" },
    { name: "West Virginia passport services overview", route: "/west-virginia" },
  ],
  fpcOfficeAddress: "7272 Wisconsin Ave, Bethesda, Maryland 20814",
  fpcOfficeNote: "Fast Passport Center serves Wheeling via secure registered courier with direct access to the Washington, DC Passport Agency. Our registered couriers hand-deliver your documents — meaning you get faster results without driving 280 miles. Wheeling residents can ship from any UPS Store, FedEx Office, or post office across Ohio County.",
  fpcOfficeDistance: "Serving Wheeling, Bethlehem, Triadelphia, Elm Grove, Moundsville, and all Ohio County communities",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Wheeling and the Northern Panhandle. From the Victorian Old Town historic district to Wheeling Island, from Bethlehem and Elm Grove to the Ohio Valley communities of Moundsville and Weirton — we serve all of Ohio, Marshall, and Brooke counties. Our courier service eliminates the 280-mile drive to Washington, DC.",
  nearbyCitiesIntro: "We serve Wheeling and all surrounding communities including Bethlehem, Triadelphia, Elm Grove, Moundsville, Weirton, as well as cross-river communities in Ohio — St. Clairsville, Steubenville, and Bridgeport. We serve travelers departing from Pittsburgh International Airport and regional airports.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Wheeling as simple as possible. Because we are a registered U.S. Department of State courier (not a reseller like FedEx or Staples), we pass those savings directly to you with our best price guarantee. Most Wheeling travelers start online and ship documents overnight.",
  mistakesIntro: "Wheeling travelers encounter unique passport issues — everything from cross-state vital records confusion to PIT airport drive time coordination. Here are the most common mistakes we see.",
  testimonialsIntro: "Wheeling travelers — from corporate executives to Ohio Valley families to Wheeling Jesuit University students — trust us when their international departure is days away.",
  faqIntro: "Have questions about expedited passports in Wheeling, WV? Here are the most common ones from Northern Panhandle travelers.",
  statePageRoute: "/states/west-virginia",
  statePageName: "West Virginia",
  servicesIntro: "Whether you need a renewal for a PIT flight to London, an emergency passport for a last-minute corporate meeting, or a first-time passport for a family vacation — Fast Passport Center handles it with reserved courier submission. As a registered State Department courier, we guarantee the best price.",
  travelTiming: {
    title: "Travel & Passport Timing for Wheeling Residents",
    intro: "Wheeling's Northern Panhandle location and proximity to Pittsburgh create unique travel patterns. Here's what Wheeling travelers need to know.",
    cards: [
      {
        icon: "ri-flight-takeoff-line",
        title: "PIT — Pittsburgh International Airport",
        subtitle: "60 miles east, 60–75 minutes via I-70",
        description: "Pittsburgh International is Wheeling's primary international gateway. With direct flights to London, Paris, Frankfurt, Cancun, and 50+ international destinations, PIT serves the entire Northern Panhandle. The 60-mile drive via I-70 takes about 60–75 minutes, and combined with international check-in requirements means Wheeling travelers should plan for 3.5–4 hours total from door to gate.",
        tip: "Morning PIT international departures mean leaving Wheeling by 5:00 AM. Factor this into your passport receipt timing — you need your passport in hand before any pre-dawn airport departure.",
      },
      {
        icon: "ri-building-2-line",
        title: "Corporate & Financial Services Travel",
        subtitle: "WesBanco, Orrick, Williams Lea — international business",
        description: "Wheeling punches above its weight in corporate and financial services. WesBanco's regional banking operations, Orrick's legal services center, and Williams Lea's business support operations all generate international business travel. London, Dublin, Frankfurt, and Toronto are the most common destinations. Travel is frequently booked on 1–2 weeks notice for meetings, audits, and client engagements.",
        tip: "Corporate travel desks often don't check passport expiration. Self-verify your passport validity independently — especially for any country requiring 6 months validity beyond the return date.",
      },
      {
        icon: "ri-landscape-line",
        title: "Heritage & Leisure Travel",
        subtitle: "Retirement travel, family heritage trips, and Ohio Valley vacations",
        description: "Wheeling's older demographic and strong ethnic heritage communities (Italian, German, Polish, Irish) generate regular heritage travel to Europe. Family reunions in Sicily, Oktoberfest trips to Munich, ancestral village visits in Ireland, and retirement cruises through the Mediterranean are common. These trips are typically planned months in advance — but passport renewal is often left until the final weeks.",
        tip: "Heritage travel to EU/Schengen countries requires passports valid for at least 3 months beyond your planned departure from Europe. Renew early — our standard expedited service handles planned travel with ease.",
      },
    ],
    callout: {
      icon: "ri-map-pin-2-line",
      title: "The Tri-State Reality",
      description: "Wheeling sits at the crossroads of West Virginia, Ohio, and Pennsylvania. Residents regularly cross state lines for work, healthcare, shopping, and services — and this creates unique documentation challenges for passport applications. We are experienced with the multi-state vital records, DMV, and residency verification requirements common to this region.",
    },
  },
  destinations: {
    title: "Where Wheeling Travelers Go",
    intro: "Wheeling's corporate, heritage, and family travel creates distinct international patterns. Here are the most common destinations.",
    stats: [
      { stat: "40%", label: "of Wheeling expedited requests are corporate and business-related" },
      { stat: "2 Weeks", label: "average lead time from booking to departure for Wheeling business travelers" },
      { stat: "Mar–Jun", label: "peak season with spring business travel and summer heritage/family trips" },
      { stat: "4 Days", label: "shortest turnaround for a WesBanco executive traveling to Dublin" },
    ],
    destinations: [
      { name: "Ireland & United Kingdom", context: "WesBanco and Orrick corporate travel, PIT-London direct flight, heritage tourism", urgency: "Business meetings booked 1–2 weeks out with fixed dates", icon: "ri-map-pin-2-line" },
      { name: "Germany", context: "German heritage travel to Bavaria and Rhineland, corporate supplier visits, industrial trade fairs", urgency: "Oktoberfest and holiday market bookings peak mid-summer", icon: "ri-map-pin-2-line" },
      { name: "Italy", context: "Italian-American heritage trips to Sicily, Naples, and Rome; family reunions and ancestral visits", urgency: "Heritage trips booked months ahead but passport renewal often delayed", icon: "ri-map-pin-2-line" },
      { name: "Canada", context: "Toronto business travel, Niagara Falls family trips, cross-border corporate meetings", urgency: "Last-minute business bookings common — 1 week notice", icon: "ri-map-pin-2-line" },
      { name: "Caribbean & Mexico", context: "Cruise departures, all-inclusive resort vacations, winter escape travel", urgency: "Spring break and winter escape bookings peak November–January", icon: "ri-map-pin-2-line" },
      { name: "France", context: "PIT-Paris direct flight, corporate and leisure travel, wine country tourism", urgency: "Summer travel booked 4–6 weeks out", icon: "ri-map-pin-2-line" },
    ],
    crossLinksTitle: "Explore Other West Virginia Service Areas",
    crossLinksDesc: "We serve travelers across the Mountain State.",
    crossLinks: [
      { name: "Charleston", route: "/charleston" },
      { name: "Morgantown", route: "/morgantown" },
    ],
  },
};